import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalyticsSummary, getRawAnalytics, clearAnalytics } from '../utils/analytics';
import './AnalyticsDashboard.css';

function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const communityNames = {
    indian: 'Indian Students',
    pakistani: 'Pakistani Students',
    brazilian: 'Brazilian Students',
    mexican: 'Mexican Students',
    african: 'African Students',
    chinese: 'Chinese Students',
    korean: 'Korean Students',
    filipino: 'Filipino Students',
    caribbean: 'Caribbean Students',
    european: 'European Students',
    arab: 'Arab Students',
    latinx: 'LatinX Students',
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = () => {
    setSummary(getAnalyticsSummary());
    setRawData(getRawAnalytics());
  };

  const handleClearAnalytics = () => {
    if (window.confirm('Are you sure you want to clear all analytics data?')) {
      clearAnalytics();
      loadAnalytics();
    }
  };

  if (!summary) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <header className="analytics-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back
        </button>
        <div className="header-title">
          <h1>📊 Analytics Dashboard</h1>
          <span>ONTAP Global Engagement</span>
        </div>
        <button className="refresh-btn" onClick={loadAnalytics}>
          🔄
        </button>
      </header>

      {/* Summary Stats */}
      <section className="stats-section">
        <div className="stat-card primary">
          <span className="stat-value">{summary.totalCommunityViews}</span>
          <span className="stat-label">Total Community Views</span>
        </div>
        <div className="stat-card success">
          <span className="stat-value">{summary.totalWhatsAppClicks}</span>
          <span className="stat-label">WhatsApp Clicks</span>
        </div>
        <div className="stat-card info">
          <span className="stat-value">{summary.conversionRate}%</span>
          <span className="stat-label">Conversion Rate</span>
        </div>
      </section>

      {/* Community Rankings */}
      <section className="rankings-section">
        <h2>🏆 Top Communities by Views</h2>
        <div className="rankings-list">
          {summary.communityRankings.length === 0 ? (
            <div className="no-data">No community views yet. Start exploring!</div>
          ) : (
            summary.communityRankings.map((community, index) => (
              <div key={community.id} className="ranking-item">
                <span className="rank">#{index + 1}</span>
                <div className="ranking-info">
                  <span className="ranking-name">
                    {communityNames[community.id] || community.id}
                  </span>
                  <div className="ranking-stats">
                    <span className="views">👀 {community.views} views</span>
                    <span className="clicks">💬 {community.whatsappClicks} WhatsApp clicks</span>
                  </div>
                </div>
                <div className="ranking-bar">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${(community.views / (summary.communityRankings[0]?.views || 1)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Feature Rankings */}
      <section className="rankings-section">
        <h2>⭐ Feature Interest</h2>
        <div className="feature-grid">
          {summary.featureRankings.length === 0 ? (
            <div className="no-data">No feature clicks yet.</div>
          ) : (
            summary.featureRankings.map((feature) => (
              <div key={feature.name} className="feature-stat">
                <span className="feature-name">{feature.name}</span>
                <span className="feature-clicks">{feature.clicks} clicks</span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Insights */}
      <section className="insights-section">
        <h2>💡 Insights</h2>
        <div className="insights-grid">
          {summary.communityRankings.length > 0 && (
            <div className="insight-card">
              <span className="insight-icon">🥇</span>
              <div className="insight-content">
                <h3>Most Popular Community</h3>
                <p>{communityNames[summary.communityRankings[0]?.id] || 'N/A'}</p>
              </div>
            </div>
          )}
          {summary.featureRankings.length > 0 && (
            <div className="insight-card">
              <span className="insight-icon">🎯</span>
              <div className="insight-content">
                <h3>Most Requested Feature</h3>
                <p>{summary.featureRankings[0]?.name || 'N/A'}</p>
              </div>
            </div>
          )}
          <div className="insight-card">
            <span className="insight-icon">📈</span>
            <div className="insight-content">
              <h3>Conversion Rate</h3>
              <p>{summary.conversionRate}% of visitors click WhatsApp</p>
            </div>
          </div>
        </div>
      </section>

      {/* Raw Data Toggle */}
      <section className="raw-section">
        <button className="toggle-raw" onClick={() => setShowRaw(!showRaw)}>
          {showRaw ? 'Hide' : 'Show'} Raw Data
        </button>
        {showRaw && (
          <pre className="raw-data">
            {JSON.stringify(rawData, null, 2)}
          </pre>
        )}
      </section>

      {/* Actions */}
      <section className="actions-section">
        <button className="clear-btn" onClick={handleClearAnalytics}>
          🗑️ Clear All Analytics
        </button>
        <p className="last-updated">
          Last updated: {new Date(summary.lastUpdated).toLocaleString()}
        </p>
      </section>
    </div>
  );
}

export default AnalyticsDashboard;
